import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Content,
  Needle,
  PrizeIcon,
  WheelImages,
  WheelImg,
  WheelLight,
  WheelShadow,
  WheelWrapper,
} from './Wheel.styles';
import spinImage from 'assets/wheel/spin.png';
import needle from 'assets/wheel/needle.png';
import light from 'assets/wheel/lighting.png';
import shadow from 'assets/wheel/shadow.png';
//icons
import coin from 'assets/icons/coins/coin.png';
import coins from 'assets/icons/coins/coins.png';
import sweetSmile from 'assets/wheel/sweet_smile.png';
import globe from 'assets/icons/objects/kora.png';
import samaka from 'assets/icons/objects/samaka.png';
import manita from 'assets/icons/objects/manita.png';
import badge from 'assets/icons/badges/badge.png';
import but from 'assets/icons/map/but.png';

import {
  PrizeObjectTypes,
  PrizesByOrderTypes,
  WheelProps,
} from './Wheel.types';
import Modal from 'components/Modal';
import WinningPopup from 'components/WinningPopup';
import ContentWrapper from 'components/ContentWrapper';
import usePersonalAssistant from 'store/usePesonalAssistant';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { WinningLottie } from 'components/LottieFiles';
import useSound from 'use-sound';
import collectCoins from 'assets/sounds/collectCoins.wav';
import WheelSound from 'assets/sounds/wheel.wav';

//array of the circle angles
const icons = [
  {
    icon: coin,
    alt: 'coin',
    prize: '10 coins',
    angle: 0,
  },
  {
    icon: badge,
    alt: '',
    prize: 'badge',
    angle: 45,
  },
  {
    icon: sweetSmile,
    alt: '',
    prize: 'good luck next time',
    angle: 90,
  },
  {
    icon: manita,
    alt: '',
    prize: 'manita',
    angle: 135,
  },
  {
    icon: samaka,
    alt: '',
    prize: 'samaka',
    angle: 180,
  },
  {
    icon: globe,
    alt: '',
    prize: 'globe',
    angle: 225,
  },
  {
    icon: coins,
    alt: '',
    prize: '30 coins',
    angle: 270,
  },
  {
    icon: but,
    alt: '',
    prize: 'but',
    angle: 315,
  },
];

const prizesByOrder: PrizesByOrderTypes = {
  '1': 'globe',
  '2': 'samaka',
  '3': 'manita',
  '4': 'good luck next time',
  '5': 'badge',
  '6': '10 coins',
  '7': 'but',
  '8': '30 coins',
};
const Wheel = ({ ...rest }: WheelProps) => {
  const [playCollect] = useSound(collectCoins);
  const [playWheel] = useSound(WheelSound);
  const { t } = useTranslation();
  const { pa } = usePersonalAssistant();
  const [disbaled, setDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [prize, setPrize] = useState('-');
  const [deg, setDeg] = useState(0);
  const segementSize = 360 / 8; //deg

  const wheelRef = useRef<HTMLDivElement>(null);

  const getPrizeObject = (prize: string): PrizeObjectTypes => {
    const prizeObject = icons.find((item) => item.prize === prize);
    if (prizeObject) {
      return prizeObject;
    } else {
      return icons[0];
    }
  };

  const getPrize = useCallback(
    (prizeDeg: number) => {
      const prizeKey = Math.ceil(prizeDeg / segementSize)?.toString();
      const prize = prizesByOrder[prizeKey];
      return prize;
    },
    [segementSize],
  );

  const replaceWonPrize = (prizeDeg: number, prize: string) => {
    const prizeKey = Math.ceil(prizeDeg / segementSize)?.toString();
    if (prize !== 'good luck next time') {
      const prizeIndex = icons.findIndex((item) => item.prize === prize);
      icons[prizeIndex].icon = sweetSmile;
      prizesByOrder[prizeKey] = 'good luck next time';
    }
  };

  const collectRewards = () => {
    playCollect();
    setIsOpen(false);
    replaceWonPrize(deg, prize);
  };
  const spin = () => {
    if (wheelRef && wheelRef.current) {
      playWheel();
      const wheel = wheelRef.current;
      setDisabled(true);
      //calculate rotation  degree between the min and max, and animate to it
      let newDeg = Math.floor(600 + Math.random() * 1500);
      setDeg(newDeg);
      wheel.style.transition = 'all 5s ease-out';
      wheel.style.transform = `rotate(${newDeg}deg)`;
    }
  };

  useEffect(() => {
    let wheel: HTMLDivElement | null = null;
    if (wheelRef && wheelRef.current) {
      wheel = wheelRef.current;
    }
    const handleSpinFinished = () => {
      if (wheel) {
        const currentdeg = deg % 360;
        wheel.style.transition = 'none';
        wheel.style.transform = `rotate(${currentdeg}deg)`;
        const prize = getPrize(currentdeg);
        setDeg(currentdeg);
        setPrize(prize);
        setTimeout(() => {
          setIsOpen(true);
        }, 1000);
        setDisabled(false);
      }
    };
    wheel?.addEventListener('transitionend', handleSpinFinished);

    return () => {
      wheel?.removeEventListener('transitionend', handleSpinFinished);
    };
  }, [deg, getPrize]);
  return (
    <ContentWrapper hasSpeechBox={false} avatar={pa?.avatar} {...rest}>
      <Content>
        <WheelWrapper>
          <WheelImages ref={wheelRef}>
            <WheelImg src={spinImage} alt="spin" />
            {icons.map((prize, i) => (
              <PrizeIcon
                key={i}
                src={prize.icon}
                alt={prize.alt}
                angle={prize.angle}
              />
            ))}
          </WheelImages>
          <WheelLight src={light} alt="wheel-light" />
          <WheelShadow src={shadow} alt="wheel-shadow" />
          <Needle src={needle} alt="needle" />
        </WheelWrapper>
        <Button
          width="90%"
          rotation="-1.89deg"
          shadowColor="black"
          disabled={disbaled}
          onClick={spin}
        >
          {t('spin')}
        </Button>
      </Content>
      <Modal isOpen={isOpen} closeModal={() => {}}>
        <>
          <WinningPopup
            isWon={prize === 'good luck next time' ? false : true}
            object={true}
            objectImage={getPrizeObject(prize).icon}
            objectName={prize}
            collectRewards={collectRewards}
          />
          {prize !== 'good luck next time' && (
            <WinningLottie className="absolute -top-[15rem] w-auto" loop />
          )}
        </>
      </Modal>
    </ContentWrapper>
  );
};

export default Wheel;
